import React from 'react'
import {
	useRoutes,
	RouteObject,
	Navigate,
	useLocation,
	useNavigate,
} from 'react-router-dom'
import {
	useAppSelector,
	useAppDispatch,
	routeActions,
	MenuTreeNodeType,
} from '@redux/index'

import { Logger } from '@utils'
import SsoConnectionProvider from '@routes/SsoConnectionProvider'
import AuthRoute from '@routes/Auth.route'
import AdminRoute from '@routes/Admin.route'
import MainRoute from '@routes/Main.route'
// @PLOP-IMPORTS

const TotalRoutes = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const navigate = useNavigate()
	const location = useLocation()

	// redux state
	const navigatePath: string = useAppSelector(state => state.route.navigatePath)
	const menuTreeList: MenuTreeNodeType[] = useAppSelector(
		state => state.admin.menuTreeList,
	)
	const contentsSiteBlackList: string[] = useAppSelector(
		state => state.route.contentsSiteBlackList,
	)

	const mainUriList: { [id: string]: string } = React.useMemo(
		() =>
			menuTreeList.reduce(
				(prev, curr) =>
					typeof curr.id === 'string' && curr.id.startsWith('OP-')
						? Object.assign(prev, { [curr.text]: curr.uri })
						: prev,
				{},
			),
		[menuTreeList],
	)

	// routes
	const totalRoutes: RouteObject[] = React.useMemo(() => {
		let newTotalRoutes: RouteObject[] = [
			{
				path: '/main/*',
				element: (
					<SsoConnectionProvider>
						<Navigate to="/auth/loading" replace={true} />
					</SsoConnectionProvider>
				),
			},
		]
		newTotalRoutes = newTotalRoutes.concat(AdminRoute)
		newTotalRoutes = newTotalRoutes.concat(MainRoute(mainUriList))
		newTotalRoutes = newTotalRoutes.concat(AuthRoute)
		return newTotalRoutes
	}, [mainUriList])

	// @PLOP-CODE

	React.useEffect(() => {
		Logger.path(location.pathname)

		const newCurrentPath: string = location.pathname
		reduxDispatch(routeActions.CURRENT_PATH(newCurrentPath))

		if (location.pathname.startsWith('/main') || location.pathname.startsWith('/admin')) {
			document.body.style.overflow = 'hidden'
			document.getElementsByTagName('html')[0].style.overflow = 'hidden'
		} else {
			document.body.style.overflow = 'visible'
			document.getElementsByTagName('html')[0].style.overflow = 'visible'
		}
	}, [location.pathname])

	React.useEffect(() => {
		const newPathList = getTotalRoutesPath(totalRoutes, contentsSiteBlackList)
		reduxDispatch(routeActions.TOTAL_ROUTE_PATHS(newPathList))
	}, [totalRoutes])

	React.useEffect(() => {
		if (navigatePath !== '') {
			navigate(navigatePath)
			reduxDispatch(routeActions.NAVIGATE_PATH(''))
		}
	}, [navigatePath])

	const Element: React.ReactElement | null = useRoutes([...totalRoutes])

	return Element ?? <></>
}

const getTotalRoutesPath = (
	totalRoutes: RouteObject[],
	contentsSiteBlackList: string[],
) => {
	let res: string[] = []
	totalRoutes.map(aRoute => {
		if (aRoute.children) {
			const childerenRes = getTotalRoutesPath(aRoute.children, [])

			res = res.concat(...childerenRes)
		}

		res.push(aRoute.path as string)
	})

	const result: string[] = [...new Set(res)]

	if (contentsSiteBlackList.length > 0) {
		const newContentsSiteBlackList = [...contentsSiteBlackList, '/']
		newContentsSiteBlackList.map((aBlackListPath: string) => {
			const idx = result.indexOf(aBlackListPath)
			if (idx !== -1) {
				result.splice(idx, 1)
			}
		})
	}

	return result
}

const RouteProvider = (): JSX.Element => {
	return <TotalRoutes />
}

export default RouteProvider
